// Utilidades específicas para mapas y monitor operativo.

const OSRM_ROUTE_BASE_URL = process.env.REACT_APP_OSRM_BASE_URL || 'https://router.project-osrm.org';
const OSRM_PROFILE = process.env.REACT_APP_OSRM_PROFILE || 'driving';
const GEOAPIFY_API_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY || '';
const ROAD_ROUTE_CACHE = new Map();

export const calculateBearing = (lat1, lon1, lat2, lon2) => {
  const values = [lat1, lon1, lat2, lon2];

  if (values.some((v) => typeof v !== 'number' || Number.isNaN(v))) {
    return 0;
  }

  const toRadians = (deg) => (deg * Math.PI) / 180;
  const toDegrees = (rad) => (rad * 180) / Math.PI;

  const lat1Rad = toRadians(lat1);
  const lon1Rad = toRadians(lon1);
  const lat2Rad = toRadians(lat2);
  const lon2Rad = toRadians(lon2);

  const deltaLon = lon2Rad - lon1Rad;
  const y = Math.sin(deltaLon) * Math.cos(lat2Rad);
  const x =
    Math.cos(lat1Rad) * Math.sin(lat2Rad) -
    Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(deltaLon);

  const bearing = Math.atan2(y, x);

  return (toDegrees(bearing) + 360) % 360;
};

export const isFiniteCoord = (value) => Number.isFinite(Number(value));

export const hasValidLatLng = (latlng) =>
  !!latlng && isFiniteCoord(latlng.lat) && isFiniteCoord(latlng.lng);

export const normalizeLatLng = (latlng) =>
  hasValidLatLng(latlng)
    ? {
        lat: Number(latlng.lat),
        lng: Number(latlng.lng)
      }
    : null;

export const calculateStraightLineDistanceKm = (pointA, pointB) => {
  const normalizedA = normalizeLatLng(pointA);
  const normalizedB = normalizeLatLng(pointB);

  if (!normalizedA || !normalizedB) return null;

  const earthRadiusKm = 6371;
  const toRadians = (deg) => (deg * Math.PI) / 180;
  const dLat = toRadians(normalizedB.lat - normalizedA.lat);
  const dLng = toRadians(normalizedB.lng - normalizedA.lng);
  const lat1 = toRadians(normalizedA.lat);
  const lat2 = toRadians(normalizedB.lat);

  const haversine =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const centralAngle = 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  return earthRadiusKm * centralAngle;
};

const buildSegmentCacheKey = (fromPoint, toPoint, profile = OSRM_PROFILE) => {
  const from = normalizeLatLng(fromPoint);
  const to = normalizeLatLng(toPoint);
  if (!from || !to) return '';

  return [
    profile,
    from.lat.toFixed(6),
    from.lng.toFixed(6),
    to.lat.toFixed(6),
    to.lng.toFixed(6)
  ].join('::');
};

const normalizeRoadGeometry = (coordinates = []) => {
  if (!Array.isArray(coordinates)) return [];

  return coordinates
    .map((point) => {
      if (Array.isArray(point) && point.length >= 2) {
        const lng = Number(point[0]);
        const lat = Number(point[1]);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          return { lat, lng };
        }
      }

      return normalizeLatLng(point);
    })
    .filter(Boolean);
};

const normalizeGeoapifyGeometry = (geometry = null) => {
  if (!geometry || typeof geometry !== 'object') return [];

  if (geometry.type === 'LineString') {
    return normalizeRoadGeometry(geometry.coordinates);
  }

  if (geometry.type === 'MultiLineString' && Array.isArray(geometry.coordinates)) {
    return geometry.coordinates.flatMap((line) => normalizeRoadGeometry(line));
  }

  return [];
};

const mergeSegmentGeometry = (segments = []) => {
  return segments.reduce((acc, segment) => {
    const geometry = normalizeRoadGeometry(segment?.geometry);
    if (!geometry.length) return acc;

    if (!acc.length) return [...geometry];

    const [firstPoint, ...restPoints] = geometry;
    const lastPoint = acc[acc.length - 1];

    if (lastPoint && firstPoint && lastPoint.lat === firstPoint.lat && lastPoint.lng === firstPoint.lng) {
      acc.push(...restPoints);
      return acc;
    }

    acc.push(...geometry);
    return acc;
  }, []);
};

const buildStraightFallbackSegment = (fromPoint, toPoint) => {
  const from = normalizeLatLng(fromPoint);
  const to = normalizeLatLng(toPoint);

  if (!from || !to) return null;

  return {
    distanceKm: calculateStraightLineDistanceKm(from, to) || 0,
    durationMin: null,
    geometry: [from, to],
    source: 'straight'
  };
};

export const fetchRoadRouteSegment = async (fromPoint, toPoint, options = {}) => {
  const from = normalizeLatLng(fromPoint);
  const to = normalizeLatLng(toPoint);
  if (!from || !to) return null;

  if (from.lat === to.lat && from.lng === to.lng) {
    return {
      distanceKm: 0,
      durationMin: 0,
      geometry: [from],
      source: 'same-point'
    };
  }

  const profile = options.profile || OSRM_PROFILE;
  const baseUrl = (options.baseUrl || OSRM_ROUTE_BASE_URL || '').replace(/\/$/, '');
  const cacheKey = buildSegmentCacheKey(from, to, profile);

  if (!options.forceRefresh && ROAD_ROUTE_CACHE.has(cacheKey)) {
    const cachedSegment = ROAD_ROUTE_CACHE.get(cacheKey);
    if (cachedSegment?.source === 'road' || cachedSegment?.source === 'same-point') {
      return cachedSegment;
    }
  }

  const fallbackSegment = buildStraightFallbackSegment(from, to);

  const tryGeoapifyFallback = async () => {
    if (!GEOAPIFY_API_KEY) return null;

    const url =
      `https://api.geoapify.com/v1/routing?waypoints=${from.lat},${from.lng}|${to.lat},${to.lng}` +
      `&mode=drive&apiKey=${GEOAPIFY_API_KEY}`;

    const response = await fetch(url, { signal: options.signal });
    if (!response.ok) {
      throw new Error(`Geoapify HTTP ${response.status}`);
    }

    const payload = await response.json();
    const feature = Array.isArray(payload?.features) ? payload.features[0] : null;
    const geometry = normalizeGeoapifyGeometry(feature?.geometry);
    if (!geometry.length) return null;

    const distanceMeters =
      Number(feature?.properties?.distance) ||
      Number(feature?.properties?.distance_meters) ||
      0;
    const timeSeconds =
      Number(feature?.properties?.time) ||
      Number(feature?.properties?.time_seconds) ||
      0;

    return {
      distanceKm: distanceMeters / 1000,
      durationMin: timeSeconds / 60,
      geometry,
      source: 'road'
    };
  };

  try {
    const url = `${baseUrl}/route/v1/${profile}/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson&steps=false`;
    const response = await fetch(url, { signal: options.signal });

    if (!response.ok) {
      throw new Error(`OSRM HTTP ${response.status}`);
    }

    const payload = await response.json();
    const route = Array.isArray(payload?.routes) ? payload.routes[0] : null;
    const geometry = normalizeRoadGeometry(route?.geometry?.coordinates);

    const normalizedSegment = {
      distanceKm: Number(route?.distance || 0) / 1000,
      durationMin: Number(route?.duration || 0) / 60,
      geometry: geometry.length ? geometry : fallbackSegment?.geometry || [from, to],
      source: 'road'
    };

    ROAD_ROUTE_CACHE.set(cacheKey, normalizedSegment);
    return normalizedSegment;
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw error;
    }

    try {
      const geoapifySegment = await tryGeoapifyFallback();
      if (geoapifySegment) {
        ROAD_ROUTE_CACHE.set(cacheKey, geoapifySegment);
        return geoapifySegment;
      }
    } catch (geoapifyError) {
      if (geoapifyError?.name === 'AbortError') {
        throw geoapifyError;
      }
    }

    if (fallbackSegment) {
      return fallbackSegment;
    }

    return null;
  }
};

export const buildRoadRouteForStops = async (stops = [], options = {}) => {
  const normalizedStops = stops.map((stop) => normalizeLatLng(stop)).filter(Boolean);
  if (normalizedStops.length < 2) {
    return {
      distanceKm: 0,
      durationMin: 0,
      geometry: normalizedStops,
      segments: [],
      source: 'insufficient-points'
    };
  }

  const segments = [];

  for (let index = 1; index < normalizedStops.length; index += 1) {
    const segment = await fetchRoadRouteSegment(normalizedStops[index - 1], normalizedStops[index], options);
    if (segment) segments.push(segment);
  }

  return {
    distanceKm: segments.reduce((sum, segment) => sum + (Number(segment?.distanceKm) || 0), 0),
    durationMin: segments.reduce((sum, segment) => sum + (Number(segment?.durationMin) || 0), 0),
    geometry: mergeSegmentGeometry(segments),
    segments,
    source: segments.every((segment) => segment?.source === 'road') ? 'road' : 'mixed'
  };
};

const TRANSPORT_VISUALS = {
  moto: {
    key: 'moto',
    label: 'Moto',
    color: '#0891b2',
    softColor: '#ecfeff',
    path:
      'M15.75 10.5l4.724-4.724A3.375 3.375 0 0015.75 3h-2.25a3.375 3.375 0 00-3.375 3.375v2.25M9.375 10.5H5.25A2.25 2.25 0 003 12.75v4.248M9.375 10.5h-.75V8.25m-.75 0a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm9 0a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM7.5 15.75m-3 0a3 3 0 116 0 3 3 0 01-6 0zm-3 0h7.5'
  },
  camioneta: {
    key: 'camioneta',
    label: 'Camioneta',
    color: '#2563eb',
    softColor: '#eff6ff',
    path:
      'M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25'
  },
  camion: {
    key: 'camion',
    label: 'Camión',
    color: '#7c3aed',
    softColor: '#f5f3ff',
    path:
      'M3.75 15.75V8.25A2.25 2.25 0 016 6h8.25a2.25 2.25 0 012.25 2.25v1.5h1.086a2.25 2.25 0 011.977 1.176l1.29 2.58c.154.307.235.646.235.99v1.254A2.25 2.25 0 0118.84 18h-.34m-12.75 0h8.25m-8.25 0a1.5 1.5 0 103 0m-3 0a1.5 1.5 0 013 0m7.5 0a1.5 1.5 0 103 0m-3 0a1.5 1.5 0 013 0M16.5 9.75h2.25'
  },
  auto: {
    key: 'auto',
    label: 'Auto',
    color: '#0f766e',
    softColor: '#ecfdf5',
    path:
      'M10.5 19.5h3m-6.75-4.5h11.5a2.25 2.25 0 002.25-2.25v-1.375a3.375 3.375 0 00-3.375-3.375H9.75v-2.25a3.375 3.375 0 00-3.375-3.375H5.25a2.25 2.25 0 00-2.25 2.25v2.75M15 15h4.5a2.25 2.25 0 002.25-2.25V9.75a2.25 2.25 0 00-2.25-2.25H9.75a3.375 3.375 0 01-3.375-3.375V4.5m-3.75 6H7.5m3-6h2.25'
  },
  default: {
    key: 'default',
    label: 'Unidad',
    color: '#334155',
    softColor: '#f8fafc',
    path:
      'M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25'
  }
};

export const getTransportVisualConfig = (transportType = 'camioneta') => {
  const normalized = String(transportType || 'camioneta').trim().toLowerCase();
  return TRANSPORT_VISUALS[normalized] || TRANSPORT_VISUALS.default;
};

export const getTransportLabel = (transportType = 'camioneta') =>
  getTransportVisualConfig(transportType).label;

const MONITOR_ACCENT_PALETTE = [
  { color: '#2563eb', softColor: '#eff6ff' },
  { color: '#7c3aed', softColor: '#f5f3ff' },
  { color: '#0891b2', softColor: '#ecfeff' },
  { color: '#ea580c', softColor: '#fff7ed' },
  { color: '#16a34a', softColor: '#f0fdf4' },
  { color: '#dc2626', softColor: '#fef2f2' },
  { color: '#0f766e', softColor: '#ecfdf5' },
  { color: '#c026d3', softColor: '#fdf4ff' }
];

const hashText = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  let hash = 0;
  for (let i = 0; i < normalized.length; i += 1) {
    hash = (hash << 5) - hash + normalized.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

export const getMonitorAccentStyle = (routeLike = {}) => {
  const seed = [
    routeLike?.id,
    routeLike?.transporterName,
    routeLike?.name,
    routeLike?.transportKey
  ]
    .filter(Boolean)
    .join('::');

  const hashed = hashText(seed || 'monitor-default');
  return MONITOR_ACCENT_PALETTE[hashed % MONITOR_ACCENT_PALETTE.length];
};
